import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Home } from 'lucide-react';

import { IndividualTable } from '@/features/leaderboard/IndividualTable';
import { CompanyTable } from '@/features/leaderboard/CompanyTable';
import { Pagination } from '@/components/ui/Pagination';
import { usePlayers } from '@/hooks/usePlayers';
import { aggregateByCompany, sortByTotal } from '@/lib/leaderboard';
import { useUser } from '@/app/store';

import '@/styles/leaderboard.css';

type Tab = 'individual' | 'company';

export function LeaderboardPage() {
  const [tab, setTab] = useState<Tab>('individual');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { list: players, isPending } = usePlayers();
  const user = useUser();
  // INICIO va al menú del jugador si hay sesión activa, sino a la Welcome pública
  const homeHref = user ? '/menu' : '/';

  // Datos completos ordenados
  const sortedPlayers = useMemo(() => sortByTotal(players), [players]);
  const companies = useMemo(() => aggregateByCompany(players), [players]);

  // Lista activa según tab
  const activeItems = tab === 'individual' ? sortedPlayers : companies;
  const totalPages = Math.max(1, Math.ceil(activeItems.length / pageSize));
  const startIdx = (page - 1) * pageSize;
  const paginatedPlayers = useMemo(
    () => sortedPlayers.slice(startIdx, startIdx + pageSize),
    [sortedPlayers, startIdx, pageSize],
  );
  const paginatedCompanies = useMemo(
    () => companies.slice(startIdx, startIdx + pageSize),
    [companies, startIdx, pageSize],
  );

  // Reset page al cambiar tab o pageSize
  useEffect(() => { setPage(1); }, [tab]);
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [pageSize, totalPages, page]);

  return (
    <div className="lb-root">
      <div className="bg-grid" />
      <div className="bg-glow glow-top" />
      <div className="bg-glow glow-left" />

      <div className="wrap">
        {/* Header */}
        <header className="lb-header">
          <div>
            <p className="lb-pre">RANKING EN VIVO</p>
            <h1 className="lb-title">
              DATA <span className="accent">CHALLENGE</span>
            </h1>
            <p className="lb-meta">
              {players.length} participante{players.length === 1 ? '' : 's'}
              {' · '}
              {companies.length} empresa{companies.length === 1 ? '' : 's'}
              {' · '}
              <span className="live">● en vivo</span>
            </p>
          </div>

          <div className="lb-actions">
            <Link to={homeHref} className="btn btn-primary">
              <Home className="w-[16px] h-[16px]" />
              INICIO
            </Link>
          </div>
        </header>

        {/* Tabs */}
        <div className="lb-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={tab === 'individual'}
            className={`lb-tab ${tab === 'individual' ? 'active' : ''}`}
            onClick={() => setTab('individual')}
          >
            👤 INDIVIDUAL
          </button>
          <button
            role="tab"
            aria-selected={tab === 'company'}
            className={`lb-tab ${tab === 'company' ? 'active' : ''}`}
            onClick={() => setTab('company')}
          >
            🏢 EMPRESA
          </button>
        </div>

        {/* Content */}
        <div className="lb-content">
          {isPending && players.length === 0 ? (
            <div className="lb-loading">Cargando ranking...</div>
          ) : tab === 'individual' ? (
            <>
              <IndividualTable players={paginatedPlayers} startRank={startIdx} />
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={sortedPlayers.length}
                itemsPerPage={pageSize}
                onPageChange={setPage}
                onItemsPerPageChange={setPageSize}
                itemName="participantes"
              />
            </>
          ) : (
            <>
              <CompanyTable companies={paginatedCompanies} startRank={startIdx} />
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={companies.length}
                itemsPerPage={pageSize}
                onPageChange={setPage}
                onItemsPerPageChange={setPageSize}
                itemName="empresas"
              />
            </>
          )}
        </div>

      </div>
    </div>
  );
}
